import React, { useState } from 'react';
import PdfViewer from './PdfViewer';
import './CreatePost.css';
import { apiPost, apiUpload } from '../api/apiClient';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Avatar,
  TextField,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
} from '@mui/material';
import {
  PhotoCamera,
  VideoCall,
  CloudUpload,
  Add,
} from '@mui/icons-material';

// Contract:
// props: {
//   userName: string,
//   userInitials: string,
//   userProfile: { role?: string, company?: string },
//   onAddPost: (post) => void
// }
export default function CreatePost({ userName, userInitials, userProfile, onAddPost }) {
  const [newPostText, setNewPostText] = useState('');
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewCaption, setPreviewCaption] = useState('');
  const [uploading, setUploading] = useState(false);

  const openPostDialog = () => setPostDialogOpen(true);
  const closePostDialog = () => setPostDialogOpen(false);

  const handleTextPost = async () => {
    if (!newPostText.trim()) return;
    try {
      const payload = {
        post_type: 'text',
        content: newPostText.trim(),
      };
      let created = null;
      try {
        created = await apiPost('/posts', payload);
      } catch (e) {
        console.warn('Failed to create post, using fallback', e);
      }
      const fallback = {
        id: Date.now(),
        author: userName,
        authorInitials: userInitials,
        role: userProfile?.role,
        company: userProfile?.company,
        timeAgo: 'now',
        content: newPostText,
        likes: 0,
        comments: 0,
        isLiked: false,
        likedBy: [],
      };
      onAddPost?.(created || fallback);
      setNewPostText('');
      closePostDialog();
    } catch (e) {
      console.error('Create text post failed', e);
      alert('Failed to create post. Please try again.');
    }
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setPreviewFiles(Array.from(files));
      setPreviewCaption('');
      setPreviewDialogOpen(true);
    }
  };

  const handleMediaPost = async () => {
    if (previewFiles.length === 0) return;
    try {
      setUploading(true);
      const uploaded = [];
      for (const file of previewFiles) {
        const formData = new FormData();
        formData.append('files', file);
        const data = await apiUpload('/upload', formData);
        // Expected shape: { files: [{ fileName, url }] }
        if (data && Array.isArray(data.files)) {
          uploaded.push(...data.files);
        } else if (Array.isArray(data)) {
          uploaded.push(...data);
        } else if (data) {
          uploaded.push(data);
        }
      }
      // Build file_urls from various possible shapes
      const fileUrls = uploaded
        .map((u) => {
          if (!u) return null;
          if (typeof u === 'string') return u;
          // Prefer the provided response shape { fileName, url }
          if (u.url) return u.url;
          return u.fileUrl || u.secure_url || u.location || u.href || u.downloadUrl || null;
        })
        .filter(Boolean);

      // Create post via API
      const payload = {
        post_type: 'media',
        content: (previewCaption || newPostText || '').trim(),
        file_urls: fileUrls,
      };
      let created = null;
      try {
        created = await apiPost('/posts', payload);
      } catch (e) {
        console.error('Create media post failed', e);
      }

      const fallback = {
        id: Date.now(),
        author: userName,
        authorInitials: userInitials,
        role: userProfile?.role,
        company: userProfile?.company,
        timeAgo: 'now',
        content: previewCaption || newPostText,
        likes: 0,
        comments: 0,
        isLiked: false,
        likedBy: [],
        attachments: uploaded,
      };
      onAddPost?.(created || fallback);
      setPreviewDialogOpen(false);
      setPreviewFiles([]);
      setPreviewCaption('');
      setNewPostText('');
    } catch (e) {
      console.error('Media post failed', e);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {/* Start a Post Section */}
      <Card className="cp-card">
        <CardContent className="cp-card-content">
          <Box className="cp-header-row">
            {/* <Avatar className="cp-avatar">
              {userInitials}
            </Avatar> */}
            <TextField
              fullWidth
              placeholder="What's on your mind?"
              variant="outlined"
              size="small"
              value={newPostText}
              onChange={(e) => setNewPostText(e.target.value)}
              className="cp-input"
              multiline
              minRows={1}
              maxRows={4}
            />
          </Box>
          <Box className="cp-actions-row">
            <Button component="label" startIcon={<PhotoCamera />} className="cp-action-button" size="large">
              Photo
              <input type="file" accept="image/*" hidden onChange={handleFileSelect} />
            </Button>
            <Button component="label" startIcon={<VideoCall />} className="cp-action-button" size="large">
              Video
              <input type="file" accept="video/*" hidden onChange={handleFileSelect} />
            </Button>
            <Button component="label" startIcon={<CloudUpload />} className="cp-action-button" size="large">
              Document
              <input type="file" accept="application/pdf" hidden onChange={handleFileSelect} />
            </Button>
            <Box className="cp-spacer" />
            {newPostText.trim() && (
              <Button variant="contained" onClick={openPostDialog} disabled={!newPostText.trim()} className="cp-post-button" size="large" endIcon={<SendIcon />}>
                Post
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Text Post Dialog */}
      <Dialog open={postDialogOpen} onClose={closePostDialog} maxWidth="sm" fullWidth>
        <DialogTitle className="cp-dialog-title">Create a post</DialogTitle>
        <DialogContent className="cp-dialog-content">
          <Box className="cp-dialog-header">
            <Avatar className="cp-avatar-red">{userInitials}</Avatar>
            <Box className="cp-flex-grow">
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="What do you want to talk about?"
                variant="outlined"
                value={newPostText}
                onChange={(e) => setNewPostText(e.target.value)}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions className="cp-dialog-actions">
          <Button onClick={closePostDialog} className="cp-button-plain">Cancel</Button>
          <Button onClick={handleTextPost} variant="contained" disabled={!newPostText.trim()} className="cp-button-primary">
            Post
          </Button>
        </DialogActions>
      </Dialog>

      {/* Media Preview Dialog (side-by-side square preview and caption) */}
  <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle className="cp-dialog-title">Create post</DialogTitle>
        <DialogContent className="cp-dialog-content" dividers>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', gap: 3 }}>
            {/* Preview on the left, larger square */}
            <Box sx={{ flex: '0 0 400px', width: 400, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 2, overflow: 'hidden', mb: { xs: 2, md: 0 } }}>
              {previewFiles.length > 0 && (() => {
                const file = previewFiles[0];
                if (file.type.startsWith('image/')) {
                  return (
                    <Box component="img" src={URL.createObjectURL(file)} alt={file.name} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  );
                } else if (file.type.startsWith('video/')) {
                  return (
                    <video src={URL.createObjectURL(file)} controls style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  );
                } else if (file.type === 'application/pdf') {
                  return (
                    <PdfViewer
                      fileUrl={URL.createObjectURL(file)}
                      fileName={file.name}
                      maxWidth="100%"
                      minHeight="100%"
                      showNavigation={true}
                      clickToOpen={false}
                    />
                  );
                }
                return null;
              })()}
            </Box>
            {/* Caption on the right */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <TextField 
                fullWidth 
                label="Caption" 
                value={previewCaption} 
                onChange={(e) => setPreviewCaption(e.target.value)} 
                multiline 
                minRows={6} 
                maxRows={10} 
                variant="outlined"
              />
              <Box className="cp-dialog-buttons" sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={() => setPreviewDialogOpen(false)} className="cp-button-plain">Cancel</Button>
                <Button onClick={handleMediaPost} variant="contained" disabled={uploading} className="cp-button-primary">
                  {uploading ? 'Posting…' : 'Post'}
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Create Post FAB */}
      <Fab color="primary" aria-label="create post" onClick={openPostDialog} className="cp-fab">
        <Add />
      </Fab>
    </>
  );
}
